import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, TrendingUp, Award, Crown, Zap } from 'lucide-react';

interface PerformerData {
  label: string;
  value: number;
  sublabel?: string;
}

interface PerformerShowcaseProps {
  title: string;
  data: PerformerData[];
}

const PerformerShowcase: React.FC<PerformerShowcaseProps> = ({ title, data }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Sort data by value descending to get rankings
  const sortedData = [...data].sort((a, b) => b.value - a.value);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 0: return <Crown className="w-8 h-8" />;
      case 1: return <Trophy className="w-7 h-7" />;
      case 2: return <Award className="w-6 h-6" />;
      default: return <Star className="w-5 h-5" />;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 0: return { bg: 'from-yellow-400 to-amber-500', text: 'text-yellow-600', glow: 'shadow-yellow-500/50' };
      case 1: return { bg: 'from-gray-300 to-gray-400', text: 'text-gray-600', glow: 'shadow-gray-400/50' };
      case 2: return { bg: 'from-orange-400 to-orange-500', text: 'text-orange-600', glow: 'shadow-orange-500/50' };
      default: return { bg: 'from-blue-400 to-blue-500', text: 'text-blue-600', glow: 'shadow-blue-500/50' };
    }
  };

  const getPerformanceLevel = (value: number) => {
    if (value >= 90) return { label: 'Exceptional', color: 'text-green-600', icon: <Zap className="w-4 h-4" /> };
    if (value >= 75) return { label: 'Excellent', color: 'text-blue-600', icon: <TrendingUp className="w-4 h-4" /> };
    if (value >= 50) return { label: 'Good', color: 'text-yellow-600', icon: <Star className="w-4 h-4" /> };
    return { label: 'Needs Improvement', color: 'text-red-600', icon: <TrendingUp className="w-4 h-4" /> };
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 shadow-xl border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <span>Top {sortedData.length} Performers</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {sortedData.map((performer, index) => {
          const rankColors = getRankColor(index);
          const performance = getPerformanceLevel(performer.value);
          const isHovered = hoveredIndex === index;

          return (
            <motion.div
              key={index}
              className="relative"
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5, type: 'spring' }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
            >
              {/* Rank Badge */}
              <motion.div
                className={`absolute -top-4 -right-4 z-10 w-12 h-12 rounded-full bg-gradient-to-br ${rankColors.bg} 
                  flex items-center justify-center text-white shadow-lg ${rankColors.glow}`}
                animate={{
                  scale: isHovered ? 1.2 : 1,
                  rotate: isHovered ? 360 : 0,
                }}
                transition={{ duration: 0.5 }}
              >
                {getRankIcon(index)}
              </motion.div>

              {/* Card */}
              <motion.div
                className="relative bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 overflow-hidden"
                whileHover={{ 
                  scale: 1.05, 
                  y: -10,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                {/* Animated Background Gradient */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${rankColors.bg} opacity-0`}
                  animate={{ opacity: isHovered ? 0.1 : 0 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Content */}
                <div className="relative z-10">
                  {/* Rank Number */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-6xl font-black ${rankColors.text} opacity-20`}>
                      #{index + 1}
                    </span>
                  </div>

                  {/* Name */}
                  <h4 className="text-lg font-bold text-gray-900 mb-2 truncate">
                    {performer.label}
                  </h4>

                  {/* Performance Score */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-black text-gray-900">
                        {performer.value}
                      </span>
                      <span className="text-lg text-gray-500">%</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className={`absolute inset-y-0 left-0 bg-gradient-to-r ${rankColors.bg} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${performer.value}%` }}
                        transition={{ delay: index * 0.1 + 0.3, duration: 1, ease: 'easeOut' }}
                      />
                      {/* Shimmer effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                        animate={{
                          x: ['-100%', '200%'],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatDelay: 1,
                        }}
                      />
                    </div>
                  </div>

                  {/* Performance Level Badge */}
                  <div className={`flex items-center gap-2 ${performance.color} mb-3`}>
                    {performance.icon}
                    <span className="text-sm font-semibold">{performance.label}</span>
                  </div>

                  {/* Sublabel */}
                  {performer.sublabel && (
                    <div className="text-sm text-gray-500 font-medium">
                      {performer.sublabel}
                    </div>
                  )}

                  {/* Hover Stats */}
                  <AnimatePresence>
                    {isHovered && (
                      <motion.div
                        className="mt-4 pt-4 border-t border-gray-200"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-center p-2 bg-gray-50 rounded-lg">
                            <div className="font-bold text-gray-900">Rank</div>
                            <div className="text-gray-600">#{index + 1}</div>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded-lg">
                            <div className="font-bold text-gray-900">Score</div>
                            <div className="text-gray-600">{performer.value}%</div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Particle Effects on Hover */}
                {isHovered && index === 0 && (
                  <>
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                        initial={{
                          x: '50%',
                          y: '50%',
                          scale: 0,
                          opacity: 1,
                        }}
                        animate={{
                          x: `${50 + Math.cos(i * 60 * Math.PI / 180) * 100}%`,
                          y: `${50 + Math.sin(i * 60 * Math.PI / 180) * 100}%`,
                          scale: [0, 1, 0],
                          opacity: [1, 1, 0],
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          delay: i * 0.1,
                        }}
                      />
                    ))}
                  </>
                )}
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <motion.div
        className="mt-8 grid grid-cols-3 gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-200">
          <div className="text-2xl font-bold text-yellow-600">
            {sortedData[0]?.value || 0}%
          </div>
          <div className="text-xs text-gray-600 mt-1">Top Score</div>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
          <div className="text-2xl font-bold text-blue-600">
            {Math.round(sortedData.reduce((sum, p) => sum + p.value, 0) / sortedData.length)}%
          </div>
          <div className="text-xs text-gray-600 mt-1">Average</div>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
          <div className="text-2xl font-bold text-green-600">
            {sortedData.filter(p => p.value >= 75).length}
          </div>
          <div className="text-xs text-gray-600 mt-1">High Performers</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PerformerShowcase;

